/**
 * CSS `:root` custom-property parsing for the guided theme editor (AB#624).
 *
 * Bundled themes (`default`, `minimal-newsletter`) declare their palette + fonts
 * as `--color-*` / `--font-*` custom properties in the FIRST `:root { … }` block
 * of `static/style.css`, which every template links. Editing those values in
 * place is the smallest change that actually reaches the built site — no new
 * override file to wire into `base.html`, no backend endpoint.
 *
 * These are pure string transforms (no React) so they stay unit-testable.
 */

export interface CssVar {
  name: string;
  value: string;
}

export interface RootBlock {
  /** index of the char just after the opening `{` */
  start: number;
  /** index of the closing `}` */
  end: number;
  vars: CssVar[];
}

/** Which palette a parse/apply targets. */
export type PaletteMode = 'light' | 'dark';

/** Parse the first `:root { … }` block found at or after `from`. */
function findRootBlock(css: string, from: number): RootBlock | null {
  const rootIdx = css.indexOf(':root', from);
  if (rootIdx === -1) return null;
  const open = css.indexOf('{', rootIdx);
  if (open === -1) return null;
  const close = css.indexOf('}', open);
  if (close === -1) return null;

  const inner = css.slice(open + 1, close);
  const vars: CssVar[] = [];
  const re = /(--[\w-]+)\s*:\s*([^;]+);/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(inner)) !== null) {
    vars.push({ name: m[1], value: m[2].trim() });
  }
  return { start: open + 1, end: close, vars };
}

/** Byte offset just after the `@media (prefers-color-scheme: dark) {`, or -1. */
function darkMediaStart(css: string): number {
  const m = /@media[^{]*prefers-color-scheme\s*:\s*dark[^{]*\{/i.exec(css);
  return m ? m.index + m[0].length : -1;
}

/**
 * Parse a palette's `:root { … }` custom properties. `'light'` (default) reads
 * the FIRST `:root` block; `'dark'` reads the `:root` inside the
 * `@media (prefers-color-scheme: dark)` block. Returns null when that palette's
 * block is absent (e.g. a light-only stylesheet, or a Tailwind-compiled theme) —
 * the caller then falls back to raw file editing. `:root` blocks are flat, so the
 * first `}` after the opening `{` closes the block.
 */
export function parseRootVars(css: string, mode: PaletteMode = 'light'): RootBlock | null {
  if (mode === 'dark') {
    const from = darkMediaStart(css);
    return from === -1 ? null : findRootBlock(css, from);
  }
  return findRootBlock(css, 0);
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Replace the values of the named custom properties WITHIN one palette's `:root`
 * block only, preserving every byte outside those specific values (comments,
 * whitespace, unmanaged vars, and the other palette's block). `'light'` (default)
 * edits the first `:root`; `'dark'` edits the `@media (prefers-color-scheme: dark)`
 * `:root`. Names not present in the targeted block are ignored.
 */
export function applyRootVars(
  css: string,
  edits: Record<string, string>,
  mode: PaletteMode = 'light',
): string {
  const block = parseRootVars(css, mode);
  if (!block) return css;

  let inner = css.slice(block.start, block.end);
  for (const [name, value] of Object.entries(edits)) {
    // `\s*:` after the exact name avoids the `--color-text` / `--color-text-muted`
    // prefix collision. A function replacer keeps `$` in the value literal.
    const re = new RegExp(`(${escapeRegExp(name)}\\s*:\\s*)([^;]*)(;)`);
    inner = inner.replace(re, (_full, p1: string, _old: string, p3: string) => p1 + value + p3);
  }
  return css.slice(0, block.start) + inner + css.slice(block.end);
}

export const isColorVar = (name: string) => name.includes('color');
export const isFontVar = (name: string) => name.includes('font');

/**
 * Normalize a color value to a 6-digit hex for `<input type="color">`, which
 * only accepts that form. Returns null for non-hex values (rgb(), named colors,
 * gradients) so the caller can keep the text field as the source of truth.
 */
export function toHex6(value: string): string | null {
  const s = value.trim();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(s);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s.toLowerCase();
  return null;
}
